import React, { useEffect, useState } from "react";
import Head from "next/head";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument } from 'pdf-lib';
import PDFMerger from "pdf-merger-js/browser";
import { AiOutlineSplitCells, AiOutlineCheck } from "react-icons/ai";
import { FaFileUpload } from "react-icons/fa";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const BlogData = [
    {
        id: 1,
        question: "What is Delete PDF Pages Tool?",
        answer: (
            <div className="">
                <p className="">
                    A Delete PDF Pages Tool is a tool that allows you to remove specific pages from a PDF document. With this tool, you can delete unwanted pages and save the modified PDF file without those pages.
                </p>
            </div>
        ),
    },
    {
        id: 2,
        question: "How to Use the Delete PDF Pages Tool?",
        answer: (
            <div className="">
                <p className="">
                    To use the Delete PDF Pages Tool, you need to upload the PDF file you want to edit. Then, you can select the pages you want to delete and apply the changes. The tool will save the modified PDF with the selected pages removed.
                </p>
            </div>
        ),
    },
    {
        id: 3,
        question: "Is there a Limit on the Number of Pages I Can Delete?",
        answer: (
            <div className="">
                <p className="">
                    There is typically no limit on the number of pages you can delete using the Delete PDF Pages Tool. You can remove as many pages as needed from your PDF document.
                </p>
            </div>
        ),
    },
    {
        id: 4,
        question: "Are Deleted Pages Permanently Removed?",
        answer: (
            <div className="">
                <p className="">
                    Yes, when you use the Delete PDF Pages Tool, the selected pages are permanently removed from the PDF file. Make sure to review your selections before confirming the deletion.
                </p>
            </div>
        ),
    },
    {
        id: 5,
        question: "Can I Preview the PDF Before Deleting Pages?",
        answer: (
            <div className="">
                <p className="">
                    Many Delete PDF Pages Tools offer a preview feature that allows you to view the PDF before making any changes. This helps you ensure you are deleting the correct pages from the document.
                </p>
            </div>
        ),
    },
];

const DeletePdfPages = () => {
    const [pdfFile, setPdfFile] = useState(null);
    const [numberOfPages, setNumberOfPages] = useState(null);
    const [loader, setLoader] = useState(false);
    const [pagesToSplit, setPagesToSplit] = useState({
        individualPages: [],
        pdfFiles: [],
    });
    const [error, setError] = useState("");
    const [signaturePopup, setSignaturePopup] = useState(false);

    const scrollToPreview = () => {
        const element = document.getElementById('preview-pdfs');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setPdfFile(file);
        scrollToPreview();
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files[0];
        setPdfFile(file);
        if (file) {
            scrollToPreview();
        }
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumberOfPages(numPages);
        setPagesToSplit({ ...pagesToSplit, individualPages: Array.from(new Array(numPages), (el, index) => index + 1) });
        // console.log(pagesToSplit)
    }

    const splitPdf = async () => {
        setError("")
        setLoader(true);
        if (!pdfFile) return;

        try {
            const pdfBytes = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(pdfBytes);
            const pageCount = pdfDoc.getPageCount();
            await splitAndMergePdf(pdfDoc, pageCount);

        } catch (error) {
            console.error(error);
        }
    };

    const splitAndMergePdf = async (pdfDoc, pageCount) => {
        if (pagesToSplit.individualPages.length === 0) {
            setError("You have selected all the pages to delete")
            setLoader(false)
            return;
        }
        let splitIndexArray = pagesToSplit.individualPages.sort();
        const pdfFiles = [];

        const promises = splitIndexArray.map(async (page) => {
            const newPdf = await PDFDocument.create();
            const [pdfPage] = await newPdf.copyPages(pdfDoc, [page - 1]);
            newPdf.addPage(pdfPage);

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const file = new File([blob], `page-${page}.pdf`, { type: 'application/pdf' });
            pdfFiles.push(file);
        });

        await Promise.all(promises);

        const merger = new PDFMerger();
        for (const file of pdfFiles) {
            await merger.add(file);
        }

        const mergedPdf = await merger.saveAsBlob();
        setLoader(false);
        downloadPdf(mergedPdf, 'document.pdf');
    };

    const downloadPdf = (blob, fileName) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        setLoader(false);
        setSignaturePopup(true);
    };


    const addOrRemovePage = (pageNumber) => {
        if (pagesToSplit.individualPages.includes(pageNumber)) {
            setPagesToSplit({
                ...pagesToSplit,
                individualPages: pagesToSplit.individualPages.filter((page) => page !== pageNumber),
            });
        } else {
            setPagesToSplit({
                ...pagesToSplit,
                individualPages: [...pagesToSplit.individualPages, pageNumber],
            });
        }
    }

    useEffect(() => {
        setPagesToSplit({ ...pagesToSplit, individualPages: Array.from(new Array(numberOfPages), (el, index) => index + 1), pdfFiles: [] })
    }, [pdfFile]);

    return (
        <div className="w-full mt-20 sm:mt-24">
            <Head>
                <meta charSet="utf-8" />
                <title>Delete PDF Pages Tool - BoloSign</title>
                <meta name="description" content="Delete specific pages from your PDF. Remove unwanted pages and save your modified PDF file." />
                <meta name="keywords" content="delete pdf pages, remove pdf pages, delete pages from pdf, remove pages from pdf, pdf page removal, pdf page delete, delete specific pages from pdf" />
                <meta name="author" content="BoloSign" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta property="og:title" content="Delete PDF Pages Tool - BoloSign" />
                <meta property="og:description" content="Delete specific pages from your PDF. Remove unwanted pages and save your modified PDF file." />
                <meta property="og:url" content="https://www.bolosign.com/signature/pdf-tools/delete-pdf-pages" />
                <meta property="og:site_name" content="BoloSign" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="BoloSign" />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en_US" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@BoloForms" />
                <meta name="twitter:creator" content="@BoloForms" />
                <meta name="twitter:title" content="Delete PDF Pages Tool - BoloSign" />
                <meta name="twitter:description" content="Delete specific pages from your PDF. Remove unwanted pages and save your modified PDF file." />
                <link rel="canonical" href="https://www.bolosign.com/signature/pdf-tools/delete-pdf-pages/" />
            </Head>


            <div className="">
                <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
                    <div className="text-violet-500 text-center">
                        <h1 className="text-3xl md:text-6xl text-gray-800">
                            Delete PDF Pages Tool
                        </h1>
                        <p className="text-lg md:text-2xl mt-4 text-gray-600">
                            Delete specific pages from your PDF. Remove unwanted pages and save your modified PDF file.
                        </p>
                    </div>
                    <div
                        className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
                        onDragEnter={(e) => e.preventDefault()}
                        onDragOver={(e) => e.preventDefault()}
                        onDragLeave={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">

                            <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
                            <div className="input_field flex flex-col w-full mx-auto text-center">
                                <label>
                                    <input
                                        onChange={handlePdfUpload}
                                        className="text-sm cursor-pointer w-36 hidden"
                                        type="file"
                                        accept=".pdf"
                                    />
                                    <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                                        Upload PDF File
                                    </div>
                                </label>

                                <div className="title text-gray-500 uppercase text-lg md:text-xl">
                                    or drop file here
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="preview-pdfs" className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center">
                    {pdfFile && (
                        <>
                            <h1 className="text-3xl font-light text-center text-gray-700">
                                Select pages that you want to delete from PDF
                            </h1>

                            <div
                                className="w-full h-[500px] flex flex-wrap justify-center gap-10 md:border-2 md:px-10 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px]"
                            >
                                {
                                    Array.from(new Array(numberOfPages), (el, index) =>
                                    (
                                        <div
                                            key={index}
                                            className="border drop-shadow-2xl w-fit relative"
                                        >
                                            <Document file={pdfFile} onLoadSuccess={onDocumentLoadSuccess}>
                                                <Page
                                                    pageNumber={index + 1}
                                                    width={260}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                />
                                            </Document>
                                            <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                                                {index + 1}
                                            </div>
                                            <div onClick={() => addOrRemovePage(index + 1)} className={`absolute top-2 right-2 w-8 h-8 ${pagesToSplit.individualPages.includes(index + 1) ? "bg-gray-300" : "bg-violet-500"} rounded-full text-xl text-white flex justify-center items-center cursor-pointer`}>
                                                <AiOutlineCheck />
                                            </div>
                                        </div>

                                    ))
                                }
                            </div>
                            {
                                error &&
                                <div className="text-lg flex justify-center items-center h-10">
                                    <p className="text-red-500">{error}</p>
                                </div>
                            }
                        </>
                    )}

                    {pdfFile && (
                        <div id="split-pdfs" className="w-full">
                            <button
                                onClick={splitPdf}
                                className="bg-violet-600 mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                            >
                                {loader ? (
                                    <div
                                        class={`flex items-center text-white space-x-2 ${styles.loader}`}
                                    >
                                        <div class="">Downloading</div>
                                        <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                    </div>
                                ) : (
                                    <>
                                        Download Pdf
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {
                    signaturePopup && <SignaturePopup onPopupClose={() => setSignaturePopup(false)} source="DELETE_PDF_PAGES" />
                }

                <div className="mt-20">
                    <div className="mb-20">
                        <PdfToolsLinks />
                    </div>
                    <Blog heading="Delete PDF Pages Tool FAQs" BlogData={BlogData} />
                </div>
            </div>
        </div>
    );
};

export default DeletePdfPages;

